import type { StoredTokens } from './auth.types';
import { defaultAdapters, AUTH_TOKEN_KEY } from '@infra/storage';
import { decodeJwtPayload, extractUserRoles } from '../domain/auth.domain';

const adapter = defaultAdapters.local;

export const readStoredTokens = (): StoredTokens | null => {
  return adapter.getItem<StoredTokens | null>(AUTH_TOKEN_KEY, null);
};

export const hasAuthToken = (): boolean => {
  const tokens = readStoredTokens();
  if (!tokens) {
    return false;
  }

  return Boolean(tokens.access_token ?? tokens.id_token);
};

export const getUserRoles = (): string[] => {
  const tokens = readStoredTokens();
  const payload = decodeJwtPayload(tokens?.access_token) ?? decodeJwtPayload(tokens?.id_token);
  return extractUserRoles(payload);
};

/**
 * Returns the local DB user public UUID (from `users` table) — the canonical `userPublicId`.
 * Comes from the token-exchange/refresh RESPONSE BODY: Core's canonical `userPublicId` key, or
 * the legacy `userId` key (older Core / tokens persisted before the rename). There is no JWT
 * fallback on purpose — the raw FusionAuth token does NOT carry this uuid (Core resolves it
 * server-side via DB lookup), so decoding the token for it would always be undefined.
 */
export const getUserPublicId = (): string | null => {
  const tokens = readStoredTokens();
  return tokens?.userPublicId || tokens?.userId || null;
};

/** Get FusionAuth user ID */
export const getFusionId = (): string | null => {
  const tokens = readStoredTokens();
  // Priority: 1) fusionId from token response (set by backend), 2) decoded from token (sub)
  if (tokens?.fusionId) {
    return tokens.fusionId;
  }
  const payload = decodeJwtPayload(tokens?.access_token) ?? decodeJwtPayload(tokens?.id_token);
  return payload?.sub ?? null;
};

export interface UserInfoFromToken {
  sub?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  picture?: string;
}

export const getUserInfoFromToken = (): UserInfoFromToken | null => {
  const tokens = readStoredTokens();
  // Prefer id_token for user info as it typically contains profile data
  const payload = decodeJwtPayload(tokens?.id_token) ?? decodeJwtPayload(tokens?.access_token);
  if (!payload) {
    return null;
  }

  return {
    sub: payload.sub,
    email: payload.email as string | undefined,
    name: payload.name as string | undefined,
    firstName: payload.given_name as string | undefined,
    lastName: payload.family_name as string | undefined,
    picture: payload.picture as string | undefined,
  };
};

/**
 * Persist tokens.
 *
 * Default (refresh) mode preserves "sticky" long-lived fields when the incoming
 * payload omits them (RFC 6749 §6): on a `refresh_token` grant the authorization
 * server MAY omit `refresh_token` from the response — FusionAuth's "Reusable"
 * policy does exactly this — in which case the client MUST keep reusing the one
 * it already holds. A blind overwrite would drop it, so every subsequent refresh
 * fails and the user is force-logged out when the access token expires (including
 * mid-roleplay). `id_token` is kept for the same reason: it's the `id_token_hint`
 * on logout.
 *
 * `{ replace: true }` (login) writes the incoming payload wholesale in a SINGLE
 * atomic setItem — no merge, so no field from a previous user can leak in, and no
 * clear-then-store gap where storage is momentarily empty.
 */
export const storeAuthTokens = (payload: StoredTokens, options?: { replace?: boolean }) => {
  const prev = options?.replace ? null : readStoredTokens();
  adapter.setItem(AUTH_TOKEN_KEY, {
    ...prev,
    ...payload,
    refresh_token: payload.refresh_token ?? prev?.refresh_token,
    id_token: payload.id_token ?? prev?.id_token,
  });
};

export const clearAuthTokens = () => {
  adapter.removeItem(AUTH_TOKEN_KEY);
};

export const getAccessToken = (): string | null => {
  const tokens = readStoredTokens();
  if (!tokens) {
    return null;
  }

  // support multiple token shapes
  return (tokens as any).access_token ?? (tokens as any).accessToken ?? null;
};
